import React, { useEffect, useRef } from "react";
import { t } from "@/i18n";

/* An advertisement, and it says so. The label is not a courtesy: undisclosed
   paid placement is a dark pattern, and in the EU it is also unlawful. */

const Label = () => (
  <p
    className="mb-2 text-[10px] uppercase tracking-[0.14em]"
    style={{ color: "var(--muted-ink)" }}
  >
    {t("site.sponsored")}
  </p>
);

/** A network's own tag. Injected into a container rather than rendered as JSX
    because ad scripts write themselves into the document; React would fight
    them for ownership of the node on every re-render. */
const NetworkSlot = ({ code, testId }) => {
  const host = useRef(null);

  useEffect(() => {
    const node = host.current;
    if (!node || !code) return undefined;
    node.innerHTML = "";

    const fragment = document.createRange().createContextualFragment(code);
    // createContextualFragment builds inert <script> nodes; they only run once
    // recreated, which is what the loop below does deliberately and visibly
    fragment.querySelectorAll("script").forEach((original) => {
      const script = document.createElement("script");
      Array.from(original.attributes).forEach((attr) =>
        script.setAttribute(attr.name, attr.value)
      );
      script.text = original.text;
      original.replaceWith(script);
    });
    node.appendChild(fragment);

    return () => {
      node.innerHTML = "";
    };
  }, [code]);

  return (
    <div className="my-8" data-testid={testId}>
      <Label />
      <div ref={host} />
    </div>
  );
};

const OwnSlot = ({ slot, testId }) => (
  <aside className="surface my-8 overflow-hidden rounded-xl" data-testid={testId}>
    <div className="p-5">
      <Label />
      <div className="flex flex-wrap items-center gap-5">
        {slot.image_url ? (
          <img
            src={slot.image_url}
            alt=""
            loading="lazy"
            className="h-20 w-20 shrink-0 rounded-lg object-cover"
          />
        ) : null}
        <div className="min-w-[200px] flex-1">
          {slot.title ? <p className="font-display text-xl leading-tight">{slot.title}</p> : null}
          {slot.body ? (
            <p className="mt-1 text-[13px] leading-relaxed text-[color:var(--ink-soft)]">
              {slot.body}
            </p>
          ) : null}
        </div>
        {slot.href && slot.cta ? (
          <a
            href={slot.href}
            target="_blank"
            // sponsored tells search engines this link was paid for; without it
            // the placement quietly becomes a ranking manipulation
            rel="sponsored noopener noreferrer"
            className="rounded-full px-4 py-2 text-[13px] font-medium"
            style={{ backgroundColor: "var(--ink)", color: "var(--paper)" }}
          >
            {slot.cta}
          </a>
        ) : null}
      </div>
    </div>
  </aside>
);

/** Every slot configured for one placement. Renders nothing when there are none. */
export const AdSlots = ({ site, placement }) => {
  const slots = (site?.ads?.slots || []).filter((slot) => slot.placement === placement);
  if (!slots.length) return null;
  return (
    <>
      {slots.map((slot) =>
        slot.kind === "network" ? (
          <NetworkSlot key={slot.id} code={slot.network_code} testId={`ad-${slot.id}`} />
        ) : (
          <OwnSlot key={slot.id} slot={slot} testId={`ad-${slot.id}`} />
        )
      )}
    </>
  );
};

export default AdSlots;
